import { useState } from 'react';
import { X } from 'lucide-react';
import { Card } from '../ui/Card';
import axios from 'axios';
import toast from 'react-hot-toast';

interface LogExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const COMMON_EXERCISES = [
  'Plimbare',
  'Stretching',
  'Yoga',
  'Fizioterapie',
  'Înot',
  'Exerciții ușoare',
  'Bicicletă',
  'Altele'
];

const INTENSITIES = [
  { value: 'LOW', label: 'Ușor', emoji: '😊', color: 'green' },
  { value: 'MEDIUM', label: 'Mediu', emoji: '😅', color: 'yellow' },
  { value: 'HIGH', label: 'Intens', emoji: '😰', color: 'red' }
];

// Returns local datetime string in format "YYYY-MM-DDTHH:mm" (for datetime-local input)
function getLocalDateTimeString(date = new Date()) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function LogExerciseModal({ isOpen, onClose, onSuccess }: LogExerciseModalProps) {
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    exerciseName: '',
    performedAt: getLocalDateTimeString(),
    duration: 30,
    intensity: 'LOW',
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.exerciseName.trim()) {
      toast.error('Introdu numele exercițiului');
      return;
    }

    if (formData.duration < 5) {
      toast.error('Durata minimă este 5 minute');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      
      // Convert datetime-local to ISO 8601
      const isoDateTime = new Date(formData.performedAt).toISOString();
      
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/tracking/exercises`,
        {
          ...formData,
          performedAt: isoDateTime
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Exercițiu înregistrat cu succes');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        exerciseName: '',
        performedAt: getLocalDateTimeString(),
        duration: 30,
        intensity: 'LOW',
        notes: ''
      });
    } catch (error: any) {
      console.error('Error logging exercise:', error);
      toast.error(error.response?.data?.error || 'Eroare la înregistrare');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              💪 Log Exercițiu
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Exercise Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tip Exercițiu *
              </label>
              <select
                value={formData.exerciseName}
                onChange={(e) => setFormData({ ...formData, exerciseName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">Selectează exercițiul</option>
                {COMMON_EXERCISES.map((exercise) => (
                  <option key={exercise} value={exercise}>
                    {exercise}
                  </option>
                ))}
              </select>
            </div>

            {/* Date/Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data și ora
              </label>
              <input
                type="datetime-local"
                value={formData.performedAt}
                onChange={(e) => setFormData({ ...formData, performedAt: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            {/* Duration Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durată: {formData.duration} minute
              </label>
              <input
                type="range"
                min="5"
                max="120"
                step="5"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5 min</span>
                <span>120 min</span>
              </div>
            </div>

            {/* Intensity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Intensitate *
              </label>
              <div className="grid grid-cols-3 gap-3">
                {INTENSITIES.map((intensity) => (
                  <button
                    key={intensity.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, intensity: intensity.value })}
                    className={`p-3 border-2 rounded-lg transition ${
                      formData.intensity === intensity.value
                        ? `border-${intensity.color}-500 bg-${intensity.color}-50`
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{intensity.emoji}</div>
                    <div className="text-xs font-medium">{intensity.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (opțional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Cum m-am simțit, dificultăți întâmpinate..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
